/**
 * Shared Editor pixel loader (Editor Load Performance P0, 2026-08).
 *
 * Consolidates what used to be two near-identical `loadHtmlImage`
 * implementations (`backgroundPresets.ts`, `DualPaneCutoutEditor.tsx`) —
 * the Remove Background / Background / Shadow editors all load pixels
 * through this one function now, per the investigation's "fix the shared
 * loader once, not three separate patches" instruction.
 *
 * Two behavior changes vs. the old `new Image(); img.crossOrigin=...;
 * img.src=url` approach, both required to actually *measure* (not guess)
 * where editor-open latency goes:
 *
 * 1. Loads via `fetch()` + `Blob` + `Object URL` instead of handing the
 *    remote URL straight to `<img src>` — this is the one point where the
 *    browser draws a hard line between "bytes received" (network) and
 *    "pixels decoded" (CPU), so `image_ttfb_ms` / `image_download_ms` /
 *    `decode_ms` can be measured as genuinely separate numbers instead of
 *    one opaque `img.onload`. This is also the Blob URL pattern the P0
 *    spec explicitly prefers over both a bare `<img src=remoteUrl>` (no
 *    visibility into decode time) and Base64 (never used here or anywhere
 *    in this loader).
 * 2. An in-flight de-dupe map: two calls for the exact same URL while the
 *    first is still pending (React StrictMode's dev-only double effect
 *    invocation being the main real-world trigger — see individual editor
 *    modals) reuse the same network request instead of firing a second
 *    one. `REQUEST_COUNT_BEFORE`/`REQUEST_COUNT_AFTER` in the P0 report is
 *    this in practice.
 */

import { withPreviewAccessHeaders } from '../../api/client'

export interface PixelLoadTiming {
  /** `performance.now()` when this fetch started. */
  fetchStartMs: number
  /** Time to first response byte (headers) — i.e. the backend+network leg. */
  ttfbMs: number
  /** Time from first byte to fully-received body. */
  downloadMs: number
  /** Time to decode the received bytes into a paintable image. */
  decodeMs: number
  /** `Content-Length` (or actual Blob size if the header was absent). */
  bytes: number | null
  contentType: string | null
  /** True if this call reused an in-flight request instead of issuing a new one. */
  deduped: boolean
  /** True if the browser answered a conditional GET with 304 (no body transferred). */
  notModified: boolean
}

const inflight = new Map<string, Promise<HTMLImageElement>>()
const lastTiming = new Map<string, PixelLoadTiming>()
/** Bounded metadata cache — timing entries are tiny but unbounded growth
 * across many unique asset URLs in a long Canvas session is avoidable
 * (VistoeraTASK-00122). */
const LAST_TIMING_MAX_ENTRIES = 64

function setLastTiming(url: string, timing: PixelLoadTiming): void {
  if (lastTiming.has(url)) {
    lastTiming.set(url, timing)
    return
  }
  if (lastTiming.size >= LAST_TIMING_MAX_ENTRIES) {
    const oldest = lastTiming.keys().next().value
    if (oldest !== undefined) lastTiming.delete(oldest)
  }
  lastTiming.set(url, timing)
}

/** Total real network requests issued by this loader since page load — the
 * `REQUEST_COUNT_BEFORE`/`REQUEST_COUNT_AFTER` counter. Deduped calls (an
 * in-flight reuse) deliberately do NOT increment this. */
let realRequestCount = 0

export function getMediaLoaderRequestCount(): number {
  return realRequestCount
}

export function resetMediaLoaderRequestCount(): void {
  realRequestCount = 0
}

/** Canvas Image Editor Ready-State P0 (VistoeraTASK-00070, 2026-09-17):
 * hard client-side timeout on the network leg only (never on `decode()`,
 * which has no meaningful upper bound for a legitimately huge image) —
 * without this, a stalled/hanging connection (no error, no response,
 * ever) left the caller's `sourceReady` stuck `false` forever with zero
 * signal, which is indistinguishable from "still loading" to the UI.
 * `AbortController` turns that silent hang into a real rejection so the
 * retry/error-state logic below (and ultimately the caller's explicit
 * error UI) always eventually fires — never an infinite spinner. */
const NETWORK_TIMEOUT_MS = 20000

/** Bounded retry for the network leg — Live Preview forensic (VistoeraTASK-
 * 00070) caught this exact `fetch()` intermittently failing (`TypeError:
 * Failed to fetch` — a transient edge/infra blip, confirmed by an
 * immediate manual retry succeeding) while the *editor's own* canonical
 * pixel load has always been all-or-nothing: one failed attempt left
 * `sourceReady` permanently `false` with the failure silently swallowed
 * by the caller's `.catch(() => undefined)` — pixels still visible via
 * the unrelated progressive `<img>` stand-in, but the real editable
 * canvas/mask never initializes ("visible but not ready"). A few quick
 * retries absorb exactly that class of blip without ever masking a *real*
 * failure (auth/404/genuine CORS misconfig) — those fail the same way on
 * every attempt and still surface the terminal error after this loop. */
const NETWORK_MAX_ATTEMPTS = 3
const NETWORK_RETRY_DELAYS_MS = [400, 1200]

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function fetchOnce(url: string): Promise<Response> {
  // Counts every real attempt (including retries) — a retried load
  // genuinely does issue more than one network request, and the P4/P30
  // dedup counters this backs should reflect that honestly rather than
  // hiding retries as if they were free.
  realRequestCount += 1
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), NETWORK_TIMEOUT_MS)
  try {
    // Cookie-authenticated proxy (`/api/media/chain/{id}/{kind}/pixels`) —
    // `credentials: 'include'` is the fetch() equivalent of the old
    // `img.crossOrigin = 'use-credentials'`. Same-origin only on Local
    // (relative `/api`); on Preview/Production `pixelsUrl()` resolves to an
    // absolute, cross-origin `api-preview.vistoera.com` URL, which the Online
    // Preview whole-origin gate would 401 without the header
    // `withPreviewAccessHeaders` attaches (VISTOERA_PREVIEW_DATA_RECOVERY,
    // 2026-08-21 — this raw `fetch()` was the one call site in the app that
    // didn't go through an axios instance already carrying that header).
    //
    // VistoeraTASK-00414 — `priority: 'high'` (Fetch Priority Hints,
    // Chrome/Edge 101+; silently ignored as an unknown `RequestInit` key
    // on browsers without support, never an error) is the fix for the
    // exact contention mechanism TASK-00255 already diagnosed by name in
    // its own commit message: on a busy Canvas with dozens of concurrent
    // same-origin requests (unrelated node thumbnails/previews), THIS
    // fetch — the one gating `sourceReady`/mask-init/color-brush
    // interactivity, i.e. the one the user is actively waiting on right
    // now — was competing on equal footing with purely-background loads
    // for the browser's limited per-origin connection slots, repeatedly
    // getting queued long enough to hit `NETWORK_TIMEOUT_MS` and retry
    // from scratch (TASK-00255's own measured "~62s stall" is almost
    // exactly `NETWORK_MAX_ATTEMPTS * NETWORK_TIMEOUT_MS`). Marking it
    // high-priority lets supporting browsers schedule it ahead of
    // low-priority background fetches instead of round-robining it with
    // them — purely a scheduling hint, never changes what bytes are
    // requested, decoded, or how correctness/safety gating works.
    return await fetch(
      url,
      withPreviewAccessHeaders({ credentials: 'include', signal: controller.signal, priority: 'high' } as RequestInit),
    )
  } finally {
    clearTimeout(timeoutId)
  }
}

/** Network leg only — bytes fetched, no decode. Retries transient
 * failures (rejected `fetch()`, 5xx, or the client-side timeout above);
 * never retries a clean 4xx (that's a real auth/not-found problem a
 * retry can't fix, and retrying it would only delay the terminal error
 * state the caller needs to show). */
async function fetchWithRetry(url: string): Promise<Response> {
  let lastErr: unknown
  for (let attempt = 0; attempt < NETWORK_MAX_ATTEMPTS; attempt++) {
    try {
      const resp = await fetchOnce(url)
      if (resp.ok || (resp.status >= 400 && resp.status < 500)) return resp
      lastErr = new Error(`media fetch failed: ${resp.status} ${url}`)
    } catch (err) {
      lastErr = err
    }
    if (attempt < NETWORK_MAX_ATTEMPTS - 1) await delay(NETWORK_RETRY_DELAYS_MS[attempt] ?? 1200)
  }
  throw lastErr
}

/** VistoeraTASK-00434 — shape of the "delivery pointer" JSON body the
 * SaaS `/pixels` route answers with instead of image bytes when presigned
 * R2 delivery is active (see `get_variant_pixels` backend docstring).
 * Deliberately a tiny, explicit discriminated shape — never guessed from
 * absence of an image content-type alone — so any genuinely-broken/empty
 * JSON response fails loudly instead of being silently treated as a
 * zero-byte image. */
interface MediaDeliveryPointer {
  delivery: 'redirect'
  url: string
  content_type?: string | null
}

function isMediaDeliveryPointer(value: unknown): value is MediaDeliveryPointer {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Record<string, unknown>
  return candidate.delivery === 'redirect' && typeof candidate.url === 'string' && candidate.url.length > 0
}

/** VistoeraTASK-00434 — second-hop fetch for the real image bytes once a
 * delivery pointer has named a presigned R2 GET URL. Deliberately bare:
 * no `credentials: 'include'`, no `withPreviewAccessHeaders` (R2's CORS
 * `AllowedHeaders` policy is only `Content-Type`/`x-amz-*` — any other
 * header, including the app's own preview-access header, would fail the
 * browser's preflight), no cookies at all. This separation is the entire
 * point of the pointer indirection: a `credentials: 'include'` request's
 * CORS check requires the (possibly-redirected) response to carry
 * `Access-Control-Allow-Credentials: true`, and R2 — like S3 — never
 * emits that header, so a single credentialed `fetch()` naively following
 * a raw HTTP redirect straight to R2 would fail the browser's CORS check
 * even with R2's `Access-Control-Allow-Origin` otherwise correctly
 * configured. A second, uncredentialed fetch never triggers that check. */
async function fetchBytesOnce(url: string): Promise<Response> {
  realRequestCount += 1
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), NETWORK_TIMEOUT_MS)
  try {
    return await fetch(url, { signal: controller.signal, priority: 'high' } as RequestInit)
  } finally {
    clearTimeout(timeoutId)
  }
}

/** Same bounded-retry contract as `fetchWithRetry`, for the bare R2 leg. */
async function fetchBytesWithRetry(url: string): Promise<Response> {
  let lastErr: unknown
  for (let attempt = 0; attempt < NETWORK_MAX_ATTEMPTS; attempt++) {
    try {
      const resp = await fetchBytesOnce(url)
      if (resp.ok || (resp.status >= 400 && resp.status < 500)) return resp
      lastErr = new Error(`r2 pointer fetch failed: ${resp.status}`)
    } catch (err) {
      lastErr = err
    }
    if (attempt < NETWORK_MAX_ATTEMPTS - 1) await delay(NETWORK_RETRY_DELAYS_MS[attempt] ?? 1200)
  }
  throw lastErr
}

async function fetchAndDecode(url: string): Promise<HTMLImageElement> {
  const t0 = performance.now()
  const resp = await fetchWithRetry(url)
  const hop1ElapsedMs = performance.now() - t0
  // Note: even when the backend's `/pixels` route answers a browser-cache
  // revalidation with a real 304 on the wire (see `chain_pixels`'s
  // `content_fingerprint`/`If-None-Match` handling), the Fetch API itself
  // transparently resolves this call's `resp` as the cached `200` — `fetch()`
  // never surfaces a bare 304 to caller code. `resp.ok` covers both cases.
  if (!resp.ok) {
    throw new Error(`media fetch failed: ${resp.status} ${url}`)
  }

  const firstContentType = resp.headers.get('content-type')
  let bytesResp: Response
  let ttfbMs: number
  let contentType: string | null
  let bodyStartMs: number

  if (firstContentType && firstContentType.toLowerCase().startsWith('application/json')) {
    // VistoeraTASK-00434 — presigned-R2 delivery pointer, not an image.
    // The SaaS hop above was the *authorization* leg (credentialed,
    // confirms tenant access to this asset/role) — now follow its answer
    // with the real, uncredentialed byte fetch straight to R2.
    let pointer: unknown
    try {
      pointer = await resp.json()
    } catch {
      throw new Error(`media fetch failed: invalid delivery pointer body for ${url}`)
    }
    if (!isMediaDeliveryPointer(pointer)) {
      throw new Error(`media fetch failed: unrecognized delivery pointer shape for ${url}`)
    }
    const t1 = performance.now()
    bytesResp = await fetchBytesWithRetry(pointer.url)
    const hop2TtfbMs = performance.now() - t1
    if (!bytesResp.ok) {
      throw new Error(`media fetch failed: ${bytesResp.status} (r2 delivery pointer target)`)
    }
    // Hop 1's full round trip (SaaS auth + tiny JSON body) plus hop 2's
    // own time-to-first-byte both count toward "time before real image
    // bytes start arriving" — i.e. the existing `ttfbMs` bucket, just
    // now spanning two hops instead of one. `downloadMs` below still
    // measures only the actual image-byte transfer (hop 2's body).
    ttfbMs = hop1ElapsedMs + hop2TtfbMs
    contentType = pointer.content_type ?? bytesResp.headers.get('content-type')
    bodyStartMs = t1 + hop2TtfbMs
  } else {
    bytesResp = resp
    ttfbMs = hop1ElapsedMs
    contentType = firstContentType
    bodyStartMs = t0 + hop1ElapsedMs
  }

  const contentLength = Number(bytesResp.headers.get('content-length')) || null
  const blob = await bytesResp.blob()
  const downloadMs = performance.now() - bodyStartMs
  const objectUrl = URL.createObjectURL(blob)
  const decodeStart = performance.now()
  const img = new Image()
  img.src = objectUrl
  try {
    if (typeof img.decode === 'function') {
      // `HTMLImageElement.decode()` — resolves specifically when decode
      // work finishes, giving a clean decode_ms boundary; falls back to
      // `onload` (still decode-complete-by-spec, just less precise) on
      // browsers without it.
      await img.decode()
    } else {
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = () => reject(new Error(`image decode failed: ${url}`))
      })
    }
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
  const decodeMs = performance.now() - decodeStart
  setLastTiming(url, {
    fetchStartMs: t0,
    ttfbMs,
    downloadMs,
    decodeMs,
    bytes: contentLength ?? blob.size,
    contentType,
    deduped: false,
    notModified: false,
  })
  return img
}

/** Drop-in replacement for the old per-file `loadHtmlImage(url)` — same
 * signature, same `HTMLImageElement` result (so every existing call site's
 * `img.naturalWidth`/`img.width`/`ctx.drawImage(img, ...)` usage keeps
 * working unchanged), now backed by the fetch+decode pipeline above plus
 * in-flight de-dupe. */
export function loadHtmlImage(url: string): Promise<HTMLImageElement> {
  const existing = inflight.get(url)
  if (existing) {
    const prior = lastTiming.get(url)
    if (prior) setLastTiming(url, { ...prior, deduped: true })
    return existing
  }
  const promise = fetchAndDecode(url)
  inflight.set(url, promise)
  // VistoeraTASK-00111 P56/P88 forensic finding — `promise.finally(cb)`
  // returns a SECOND, independent derived promise that also rejects
  // whenever `promise` does; nothing here ever awaited/caught THAT one,
  // so a real terminal load failure (confirmed live via this task's own
  // Playwright harness — a synthetic HTTP 500 reliably produced a
  // browser "Uncaught (in promise)" from exactly this line) always threw
  // an unhandled rejection, independent of `promise` itself (returned
  // below, and separately awaited/caught by every real caller — that
  // path was never broken). Never surfaced as an `imgedit-*` Error
  // Boundary catch (unhandled rejections are not a React render-phase
  // throw), but is a genuine uncaught-exception/console-spam defect in
  // its own right — a bookkeeping-only `.catch()` on this SECOND promise
  // fixes it without changing `promise`'s own rejection behavior at all.
  promise
    .finally(() => {
      inflight.delete(url)
    })
    .catch(() => undefined)
  return promise
}

/** The most recent real (non-deduped) fetch/decode timing recorded for this
 * exact URL, if any — `undefined` before the first successful load. */
export function getLastPixelLoadTiming(url: string): PixelLoadTiming | undefined {
  return lastTiming.get(url)
}

export type EditorType = 'transparent' | 'background' | 'shadow' | 'canvas_image_edit'

export interface EditorLoadTimingEntry {
  asset_id: string
  editor_type: EditorType
  source_kind: string
  source_url_type: string
  source_width: number
  source_height: number
  source_bytes: number | null
  metadata_ms: number | null
  image_ttfb_ms: number | null
  image_download_ms: number | null
  decode_ms: number | null
  canvas_init_ms: number | null
  first_draw_ms: number | null
  total_ms: number
  deduped_request: boolean
}

declare global {
  interface Window {
    __editorLoadTimings?: EditorLoadTimingEntry[]
    /** chain id -> most recent `editorOpen()`+`listEngines()` latency (ms) —
     * set by `ChainEditorModal`, read by `DualPaneCutoutEditor`'s own
     * timing log (the transparent editor's only real "metadata" API call;
     * Background/Shadow editors receive the chain from props already
     * in-memory, so their own logs leave `metadata_ms` as `null`). */
    __editorMetadataMs?: Record<string, number>
  }
}

/** Logs one `EDITOR_LOAD_TIMING` entry to the console in the exact schema
 * the P0 report requires, and appends it to `window.__editorLoadTimings`
 * for manual inspection (no browser automation available in this
 * environment — see report's `BROWSER_AUTOMATION_UNAVAILABLE`). Call this
 * once per editor open, after first paint. */
export function logEditorLoadTiming(entry: EditorLoadTimingEntry): void {
  if (typeof window !== 'undefined') {
    window.__editorLoadTimings = window.__editorLoadTimings || []
    window.__editorLoadTimings.push(entry)
  }
  // eslint-disable-next-line no-console
  console.log('[EDITOR_LOAD_TIMING]', entry)
}
